import { constants as CONSTANTS } from '../../../app.constants.js';
const BASE_URL = CONSTANTS.baseUrl;

message2AvatarAndBubble.$inject = ['$filter', '$translate', '$stateParams', 'UserFactory', 'StrategiesFactory'];
export function message2AvatarAndBubble($filter, $translate, $stateParams, UserFactory, StrategiesFactory) {

  function detailedTasktype(message) {
    let result = '';
    if (message.detailed_task_type) {
      // Messages not from self have a task, which has a site type and task type, nicely combined by the data formatter
      result = $translate.instant('message/detailed_task_type/' + message.detailed_task_type);
    } else {
      // messages from self don't have a task, hence we compute the message type as:
      // - if the message is private, we retrieve the message type from the site type's strategy
      // - if the message is public, we use the detailed_task_type of the reference message, if any
      //   (that is, the message to which this message is a reply)
      let taskType = null;
      if (message.is_private) {
        const strategy = StrategiesFactory.getStrategyFromType(message.site.type);
        taskType = strategy.getTaskTypeString(strategy.getDefaultPrivateTaskType());
      } else if (message.replied_task_info) {
        taskType = message.replied_task_info.task_detailed_type;
      }
      if (taskType) {
        result = $translate.instant('message/detailed_task_type/' + taskType);
      }
    }
    return result;
  }

  function ratingFilledStarsNumberAsArray(message) {
    const result = [];
    if (message.details && message.details.rating) {
      // we need as many filled star as the score
      for (let i = 1; i <= message.details.rating.value; i++) {
        result.push(i);
      }
    }
    return result;
  }

  function ratingEmptyStarsNumberAsArray(message) {
    const result = [];
    if (message.details?.rating?.scale) {
      // we need as many total stars as the scale
      for (let i = message.details.rating.value + 1; i <= message.details.rating.scale; i++) {
        result.push(i); // the value is not used but having different values in the array means we don't need to track by index in the ng-repeat
      }
    }
    return result;
  }

  function messageFromSelfStatus(message) {
    let result = '';
    if (message.replied_task_info && message.replied_task_info.action_admin_user_id) {
      const userId = message.replied_task_info.action_admin_user_id;
      const userName = message.replied_task_info.action_admin_user_name;
      const byWhom = byUserNameOrByYou(userId, userName);
      result = $translate.instant('user-profile/messages/sent') + byWhom;
    }
    return result;
  }

  function byUserNameOrByYou(userId, userName) {
    const loggedUserId = UserFactory.getLoggedUser().id;
    const isAssignedToLoggedUser = userId == loggedUserId; // double equal here as I am not 100% sure we'll always get integer ids
    return ' ' + $translate.instant('task/state_by')
      + ' ' + (isAssignedToLoggedUser ? $translate.instant('task/to_you') : userName);
  }

  function taskStatus(message) {
    const task = message.task;
    if (task) {
      let byWhom = '';
      if (task.assigned_to) {
        byWhom = byUserNameOrByYou(task.assigned_to.id, task.assigned_to.realname);
      }
      switch (task.state) {
        case 'pending':
          // for pending messages we display a warning, see function below
          return '';
        case 'assigned':
          return $translate.instant('task/state_assigned') + byWhom;
        case 'help_requested':
          return $translate.instant('task_template/help_request') + byWhom;
        case 'help_answered':
          return $translate.instant('task_template/help_request_answered');
        case 'answered':
          return $translate.instant('task_template/answered') + byWhom;
        case 'archived':
          return $translate.instant('task_template/archived') + byWhom;
        case 'archived_auto':
          return $translate.instant('task_template/archived_auto');
        case 'on_hold':
          return $translate.instant('task_template/on_hold') + byWhom;
      }
    }
    return '';
  }

  function messageStatus(message) {
    return message.is_from_self ? messageFromSelfStatus(message) : taskStatus(message);
  }

  function taskStatusWarning(message) {
    let result = '';
    if (message.task && message.task.state === 'pending') {
      result = $translate.instant('user-profile/messages/not-handled-yet');
    }
    return result;
  }

  function formatAttachments(attachments) {
    const result = [];
    if (attachments) {
      attachments.forEach(attachment => result.push(formatAttachment(attachment)));
    }
    return result;
  }

  function formatAttachment(attachment) {
    const result = {};
    const companyId = $stateParams.companyId;
    result.url = attachment.mime_type === 'link' ? attachment.link
      : (BASE_URL + companyId + '/message-attachments/' + attachment.message_index);
    result.downloadName = attachment.name;
    switch (attachment.mime_type) {
      case 'image':
      case 'audio':
      case 'video':
        result.type = attachment.mime_type;
        break;
      case 'file':
      case 'txt':
        result.type = 'download';
        result.icon = 'insert_drive_file';
        result.text = $translate.instant('attachments/File');
        break;
      case 'link':
        result.type = 'link';
        result.icon = 'link';
        result.text = $translate.instant('attachments/' + attachment.link_type);
        break;
      case 'other':
        result.type = 'download';
        result.icon = 'attach_file';
        result.text = $translate.instant('attachments/Other');
        break;
    }
    return result;
  }

  function messageContent(message) {
    const hasContent = !!message.content;
    const hasAttachment = message.attachments && message.attachments.length > 0;
    return (hasContent || hasAttachment) ? message.content : $translate.instant('no-content');
  }

  function publicOrHiddenStatus(message) {
    let result = '';
    if (message.is_private == 0) {
      result = (message.details && message.details.is_hidden) ? $translate.instant('task_template/hidden')
        : $translate.instant('task_template/public');
    }
    return result;
  }

  // Returns the id of the message of the first answer action that was performed
  // by the user to which the task is assigned 
  // (historically, the text "répondu par vous/untel" uses task.assigned_to to decide who replied)
  function answerMessageId(message) {
    let result = null;
    if (message.task && message.task.actions && message.task.assigned_to) {
      message.task.actions.forEach(action => {
        const sentByTaskAssignedUser = action && action.assigned_user
          && action.assigned_user.id === message.task.assigned_to.id;

        const hasAnswerMessageId = action && action.answered && action.answered.answers
          && action.answered.answers[0] && action.answered.answers[0].sent
          && action.answered.answers[0].sent.id;

        if (result === null && hasAnswerMessageId && sentByTaskAssignedUser) {
          result = action.answered.answers[0].sent.id;
        }
      });
    }
    return result;
  }

  function repliedToMessageId(message) {
    let result = null;
    if (message.replied_task_info) {
      result = message.replied_task_info.task_message_id;
    }
    return result;
  }

  function scrollToOtherMessage(message) {
    const messageId = message.is_from_self ? repliedToMessageId(message) : answerMessageId(message);
    const labelKey = message.is_from_self ? 'user-profile/messages/scroll-to-replied-to' : 'user-profile/messages/scroll-to-answer';
    const color = message.is_from_self ? 'default-accent-A200' : 'default-primary-500';
    let result = null;
    if (messageId) {
      result = {
        'messageId': messageId,
        'color': color,
        'label': $translate.instant(labelKey)
      }
    }
    return result;
  }

  function getAvatarUrlIfError(message) {
    let avatarUrl = '';
    if (message.is_from_self) {
      if (message.replied_task_info && message.replied_task_info.action_admin_user_id) {
        avatarUrl = UserFactory.getAvatarUrl(message.replied_task_info.action_admin_user_id);
      } else {
        avatarUrl = 'ajs-assets/img/avatars/icon_conseiller.png';
      }
    } else {
      avatarUrl = 'ajs-assets/img/avatars/icon_client.png';
    }
    return avatarUrl;
  }

  return function (message, openedFromTaskId) {
    if (message.closed) {
      return {
        textContent: $translate.instant('m/task-process/task-actions-log/conversation-closed'),
        avatarUrl: message.avatarUrl,
        avatarUrlIfError: message.avatarUrlIfError,
        isMessageDeleted: false,
        detailedTasktype: $translate.instant('message/detailed_task_type/event'),
        isMessagePublic: false,
        messageAttachments: [],
        scrollToOtherMessage: null,
        conversationClosed: true,
      };
    }
    return {
      'messageId': message.id,
      'taskId': (message.task && message.task.id) ? message.task.id : openedFromTaskId,
      'avatarUrl': message.from.details.avatarUrl,
      'avatarUrlIfError': getAvatarUrlIfError(message),
      'textContent': messageContent(message),
      'isMessageDeleted': message.is_deleted,
      'detailedTasktype': detailedTasktype(message),
      'ratingFilledStarsNumberAsArray': ratingFilledStarsNumberAsArray(message),
      'ratingEmptyStarsNumberAsArray': ratingEmptyStarsNumberAsArray(message),
      'messageStatus': messageStatus(message),
      'taskStatusWarning': taskStatusWarning(message),
      'messageTime': $filter('messageDateTime')(message.created_time),
      'messageTimeDelta': message.created_time_str.value + ' ' + message.created_time_str.unit,
      'messageExternalUrl': message.external_url,
      'isMessageFromSelf': message.is_from_self,
      'isMessagePublic': message.is_private == 0,
      'publicOrHiddenStatus': publicOrHiddenStatus(message),
      'messageAttachments': formatAttachments(message.attachments),
      'scrollToOtherMessage': scrollToOtherMessage(message),
    };
  }
}

